import React, { useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";

/**
 * Поле для подписи пальцем или мышью (без библиотек): холст, «Очистить».
 * toDataUrl() — маленький PNG (до 640 px по ширине), чтобы запрос был лёгким.
 */
const SignaturePad = forwardRef(function SignaturePad({ disabled = false, onChange }, ref) {
    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const lastRef = useRef(null);
    const [empty, setEmpty] = useState(true);

    const prepare = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ratio = Math.max(1, window.devicePixelRatio || 1);
        const rect = canvas.getBoundingClientRect();
        canvas.width = Math.round(rect.width * ratio);
        canvas.height = Math.round(rect.height * ratio);
        const ctx = canvas.getContext("2d");
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        ctx.lineWidth = 2.4;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.strokeStyle = "#111827";
    };

    useEffect(() => {
        prepare();
        const onResize = () => {
            if (empty) prepare();
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const point = (event) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return { x: event.clientX - rect.left, y: event.clientY - rect.top };
    };

    const start = (event) => {
        if (disabled) return;
        event.preventDefault();
        canvasRef.current.setPointerCapture?.(event.pointerId);
        drawingRef.current = true;
        lastRef.current = point(event);
        const ctx = canvasRef.current.getContext("2d");
        ctx.beginPath();
        ctx.arc(lastRef.current.x, lastRef.current.y, 1.1, 0, Math.PI * 2);
        ctx.fillStyle = "#111827";
        ctx.fill();
    };

    const move = (event) => {
        if (!drawingRef.current || disabled) return;
        event.preventDefault();
        const next = point(event);
        const ctx = canvasRef.current.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(lastRef.current.x, lastRef.current.y);
        ctx.lineTo(next.x, next.y);
        ctx.stroke();
        lastRef.current = next;
        if (empty) {
            setEmpty(false);
            onChange?.(false);
        }
    };

    const end = () => {
        drawingRef.current = false;
    };

    const clear = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
        setEmpty(true);
        onChange?.(true);
    };

    useImperativeHandle(ref, () => ({
        isEmpty: () => empty,
        clear,
        toDataUrl: () => {
            const canvas = canvasRef.current;
            if (!canvas || empty) return "";
            const width = Math.min(640, canvas.width);
            const height = Math.round(canvas.height * (width / canvas.width));
            const out = document.createElement("canvas");
            out.width = width;
            out.height = height;
            out.getContext("2d").drawImage(canvas, 0, 0, width, height);
            return out.toDataURL("image/png");
        },
    }));

    return (
        <div className={`order-doc-sign-pad-AO ${disabled ? "is-disabled" : ""}`}>
            <canvas
                ref={canvasRef}
                onPointerDown={start}
                onPointerMove={move}
                onPointerUp={end}
                onPointerLeave={end}
                onPointerCancel={end}
            />
            {empty ? <span className="order-doc-sign-pad-AO__hint">Распишитесь здесь пальцем или мышью</span> : null}
            <button type="button" onClick={clear} disabled={disabled || empty}>Очистить</button>
        </div>
    );
});

export default SignaturePad;
